import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { EligibilityHistoryPageDto, EligibilitySessionDto, PatientClaimDto, PatientClaimPageDto, PatientPreAuthDto, PatientPreAuthPageDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { dateOnly, dateTime, humanize, money } from '@/lib/utils';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Badge, Button, StatusBadge } from '@/components/ui/primitives';
import { eligibilityChip, remainingKey, remainingOf, validityLabel } from './eligibility-session';
import { MessagesButton } from './MessagesDrawer';
import { PartialLinesTable } from './VisitInsurance';

const PAGE_SIZE = 25;

export function SessionChip({ session }: { session: EligibilitySessionDto }) {
  const chip = eligibilityChip(session);
  const label = validityLabel(session);
  const remaining = session.state === 'valid' ? remainingOf(session.remainingSeconds) : null;
  return (
    <span className="flex flex-col items-start gap-0.5">
      <Badge variant={chip.variant}>{tr('billing', chip.key)}</Badge>
      {label.date && <span className="text-xs text-muted-foreground">{tr('billing', label.key, { date: dateOnly(label.date) })}</span>}
      {remaining && <span className="text-xs text-muted-foreground">{tr('billing', remainingKey(remaining.unit), { count: remaining.count })}</span>}
    </span>
  );
}

function LoadMore({ visible, loading, onClick }: { visible: boolean; loading: boolean; onClick: () => void }) {
  if (!visible) return null;
  return (
    <div className="mt-2 flex justify-center">
      <Button size="sm" variant="outline" loading={loading} onClick={onClick}>
        {tr('billing', 'nx_load_more')}
      </Button>
    </div>
  );
}

function EligibilityHistory({ patientId }: { patientId: string }) {
  const history = useInfiniteQuery({
    queryKey: ['patient-eligibility-history', patientId],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<EligibilityHistoryPageDto>(`/insurance/patients/${patientId}/eligibility-history${qs({ limit: PAGE_SIZE, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = history.data?.pages.flatMap((page) => page.items);
  return (
    <>
      <DataTable
        dense
        rows={rows}
        loading={history.isLoading}
        refreshing={history.isFetching && !history.isFetchingNextPage}
        error={history.error}
        rowKey={(row) => row.messageId}
        empty={tr('billing', 'nx_no_eligibility_history')}
        columns={[
          { header: tr('billing', 'when'), cell: (row) => <span className="whitespace-nowrap text-xs">{dateTime(row.createdAt)}</span> },
          { header: tr('billing', 'type'), cell: (row) => (row.purpose === 'discovery' ? tr('billing', 'nx_purpose_discovery') : tr('billing', 'nx_purpose_validation')) },
          { header: tr('billing', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
          { header: tr('billing', 'nx_detail'), cell: (row) => <span className="text-xs text-muted-foreground">{row.errorMessage ?? '—'}</span> },
          {
            header: '',
            align: 'right',
            cell: (row) => (row.coverageId ? <MessagesButton entityType="patient_coverage" entityId={row.coverageId} focusMessageId={row.messageId} title={tr('billing', 'nx_messages_for_eligibility')} /> : null),
          },
        ]}
      />
      <LoadMore visible={Boolean(history.hasNextPage)} loading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()} />
    </>
  );
}

function PreAuthAnswersDialog({ preAuth, onClose }: { preAuth: PatientPreAuthDto; onClose: () => void }) {
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={tr('billing', 'nx_preauth_answers_title', { number: preAuth.preauthNo ?? preAuth.id.slice(0, 8) })}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {tr('billing', 'close')}
        </Button>
      }
    >
      <PartialLinesTable lines={preAuth.lines} />
    </Dialog>
  );
}

function PatientPreAuths({ patientId }: { patientId: string }) {
  const [open, setOpen] = useState<PatientPreAuthDto | null>(null);
  const list = useInfiniteQuery({
    queryKey: ['patient-pre-auths', patientId],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<PatientPreAuthPageDto>(`/insurance/patients/${patientId}/pre-auths${qs({ limit: PAGE_SIZE, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((page) => page.items);
  return (
    <>
      <DataTable
        dense
        rows={rows}
        loading={list.isLoading}
        refreshing={list.isFetching && !list.isFetchingNextPage}
        error={list.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'nx_no_preauths')}
        columns={[
          { header: tr('billing', 'nx_number'), cell: (row) => <span className="font-medium">{row.preauthNo ?? '—'}</span> },
          { header: tr('billing', 'payer'), cell: (row) => row.payerName },
          {
            header: tr('billing', 'status'),
            cell: (row) => (
              <span className="flex flex-wrap items-center gap-1">
                <StatusBadge status={row.status} />
                {row.related === 'prior' && <Badge variant="info">{tr('billing', 'nx_follow_up')}</Badge>}
              </span>
            ),
          },
          { header: tr('billing', 'nx_requested'), align: 'right', cell: (row) => <span className="tabular">{money(row.requestedAmountMinor)}</span> },
          { header: tr('billing', 'nx_approved'), align: 'right', cell: (row) => <span className="tabular">{money(row.approvedAmountMinor)}</span> },
          { header: tr('billing', 'when'), cell: (row) => <span className="whitespace-nowrap text-xs">{dateOnly(row.requestedAt)}</span> },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <span className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => setOpen(row)}>
                  {tr('billing', 'nx_answers')}
                </Button>
                <MessagesButton entityType="pre_authorization" entityId={row.id} title={tr('billing', 'nx_messages_for_preauth', { number: row.preauthNo ?? row.id.slice(0, 8) })} />
              </span>
            ),
          },
        ]}
      />
      <LoadMore visible={Boolean(list.hasNextPage)} loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} />
      {open && <PreAuthAnswersDialog preAuth={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function PatientClaims({ patientId }: { patientId: string }) {
  const list = useInfiniteQuery({
    queryKey: ['patient-claims', patientId],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<PatientClaimPageDto>(`/insurance/patients/${patientId}/claims${qs({ limit: PAGE_SIZE, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows: PatientClaimDto[] | undefined = list.data?.pages.flatMap((page) => page.items);
  return (
    <>
      <DataTable
        dense
        rows={rows}
        loading={list.isLoading}
        refreshing={list.isFetching && !list.isFetchingNextPage}
        error={list.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'nx_no_claims')}
        columns={[
          { header: tr('billing', 'nx_number'), cell: (row) => <span className="font-medium">{row.number}</span> },
          { header: tr('billing', 'payer'), cell: (row) => row.payerName },
          { header: tr('billing', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
          { header: tr('billing', 'nx_claimed'), align: 'right', cell: (row) => <span className="tabular">{money(row.claimedAmountMinor)}</span> },
          { header: tr('billing', 'nx_approved'), align: 'right', cell: (row) => <span className="tabular">{money(row.approvedAmountMinor)}</span> },
          { header: tr('billing', 'paid'), align: 'right', cell: (row) => <span className="tabular">{money(row.paidAmountMinor)}</span> },
          { header: tr('billing', 'nx_submitted'), cell: (row) => (row.submittedAt ? <span className="whitespace-nowrap text-xs">{dateOnly(row.submittedAt)}</span> : humanize(row.exchangeState)) },
          { header: '', align: 'right', cell: (row) => <MessagesButton entityType="claim" entityId={row.id} title={tr('billing', 'nx_messages_for_claim', { number: row.number })} /> },
        ]}
      />
      <LoadMore visible={Boolean(list.hasNextPage)} loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} />
    </>
  );
}

export type PatientInsuranceView = 'policies' | 'history' | 'preauths' | 'claims';

export function PatientInsuranceView({ view, patientId }: { view: Exclude<PatientInsuranceView, 'policies'>; patientId: string }) {
  if (view === 'history') return <EligibilityHistory patientId={patientId} />;
  if (view === 'preauths') return <PatientPreAuths patientId={patientId} />;
  return <PatientClaims patientId={patientId} />;
}
